/**
 * Phase 107 (Plan 107-07 / D-15) — Timezone conversion utilities for the SLA
 * Pricing Configurator's grid hour-axis column extension.
 *
 * Ported near-verbatim from the deleted `frontend/src/components/coverage/tzUtils.ts`
 * (git show bec322bc), Phase 54 / COVERAGE-09. All conversion uses the
 * built-in `Intl` browser API — no third-party date/timezone library of any
 * kind is added, per this phase's no-new-dependency constraint.
 *
 * The single behavioural generalisation over the deleted version (D-15,
 * 107-UI-SPEC.md Implementation Note 2): the module-level Prague-only zone
 * constant becomes a `baseZone` parameter, sourced from the configuration's
 * own `baseTimezone` rather than hardcoded. `DEFAULT_BASE_ZONE` is the
 * fallback used when a configuration has none.
 *
 * This module is pure display math: it imports nothing, performs no I/O and
 * exports no function that writes, mutates or persists anything. Per D-15,
 * switching the timezone view must never mutate stored cells — there is no
 * write surface here to do so (T-107-TZWRITE).
 *
 * The primary export is `convertHourToZone(baseHour, targetZone, baseZone)`
 * which returns a DST-correct interval label (e.g. "22:00–23:00") and a
 * dayOffset (-1, 0, or +1) indicating whether the interval falls on a
 * different calendar day than the base-zone anchor row.
 */

/** Fallback base timezone when a configuration has none set (D-15). */
export const DEFAULT_BASE_ZONE = 'Europe/Prague';

/**
 * Curated list of IANA timezone ids for a follow-the-sun support organisation.
 * The configuration's own base zone is NOT included here — it is the pinned
 * anchor, always shown separately.
 */
export const CURATED_ZONES: { id: string; label: string }[] = [
  { id: 'UTC', label: 'UTC' },
  { id: 'Europe/London', label: 'UK (London)' },
  { id: 'America/New_York', label: 'US Eastern' },
  { id: 'America/Chicago', label: 'US Central' },
  { id: 'America/Denver', label: 'US Mountain' },
  { id: 'America/Los_Angeles', label: 'US Pacific' },
  { id: 'Asia/Kolkata', label: 'India (IST)' },
  { id: 'Asia/Tokyo', label: 'Japan (JST)' },
  { id: 'Australia/Sydney', label: 'Australia (AEST)' },
];

/**
 * Returns the UTC offset in minutes for a given IANA timezone at the given
 * UTC instant by comparing how that instant formats in UTC vs the target zone.
 *
 * Positive = ahead of UTC (e.g. UTC+2 returns +120), negative = behind UTC.
 */
function getUtcOffsetMinutes(zone: string, utcMs: number): number {
  // Format the same UTC instant in both UTC and the target zone
  const fmtOptions: Intl.DateTimeFormatOptions = {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  const utcFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const zoneFormatter = new Intl.DateTimeFormat('en-GB', fmtOptions);

  const utcParts = utcFormatter.formatToParts(utcMs);
  const zoneParts = zoneFormatter.formatToParts(utcMs);

  const utcH = parseInt(utcParts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const utcM = parseInt(utcParts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const zoneH = parseInt(zoneParts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const zoneM = parseInt(zoneParts.find((p) => p.type === 'minute')?.value ?? '0', 10);

  let diff = (zoneH * 60 + zoneM) - (utcH * 60 + utcM);
  // Handle wrap-around (e.g. 23:00 UTC vs 01:00 next day in zone)
  if (diff > 720) diff -= 1440;
  if (diff < -720) diff += 1440;
  return diff;
}

/**
 * Returns the calendar date string "YYYY-MM-DD" for a UTC timestamp in the
 * given IANA timezone, using the en-CA locale which produces that format natively.
 */
function getLocalDate(utcMs: number, zone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(utcMs);
}

/**
 * Formats a UTC millisecond timestamp as "HH:MM" in the given IANA timezone.
 */
function formatTime(utcMs: number, zone: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(utcMs);
  const h = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const m = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${h}:${m}`;
}

/**
 * Converts a base-zone wall-clock hour (0–23) to the equivalent interval in
 * `targetZone`, DST-correct for the current date.
 *
 * @param baseHour   - the wall-clock hour (0 = 00:00–01:00, 23 = 23:00–00:00)
 *   in `baseZone`, which is the zone the configuration's cell coordinates
 *   are stored in
 * @param targetZone - IANA timezone id for the target column
 * @param baseZone   - IANA timezone id the `baseHour` is expressed in
 *   (defaults to `DEFAULT_BASE_ZONE`)
 * @returns { label, dayOffset } where label is "HH:00–HH:00" (en-dash U+2013)
 *   and dayOffset is -1, 0, or +1 relative to the base-zone calendar day.
 */
export function convertHourToZone(
  baseHour: number,
  targetZone: string,
  baseZone: string = DEFAULT_BASE_ZONE,
): { label: string; dayOffset: number } {
  try {
    const now = new Date();

    // Midnight UTC on the current date
    const midnightUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    // Build the UTC instant that represents the START of baseHour in baseZone.
    // WR-01: resolve the base zone's UTC offset at the instant being
    // converted, not once at UTC midnight — on DST changeover days the
    // offset changes mid-day, so a single midnight sample mislabels every
    // row after the switch. Two-pass fixed-point: guess the instant using
    // the midnight offset, then re-derive the offset at the guessed instant.
    // Offsets change at most once a day, so a single refinement pass
    // converges.
    const guessOffsetMinutes = getUtcOffsetMinutes(baseZone, midnightUtc);
    const guessUtcMs = midnightUtc + baseHour * 60 * 60 * 1000 - guessOffsetMinutes * 60 * 1000;
    const baseOffsetMinutes = getUtcOffsetMinutes(baseZone, guessUtcMs);

    // The base-zone wall-clock `baseHour:00` corresponds to this UTC ms:
    const startUtcMs = midnightUtc + baseHour * 60 * 60 * 1000 - baseOffsetMinutes * 60 * 1000;
    const endUtcMs = startUtcMs + 60 * 60 * 1000;

    const startLabel = formatTime(startUtcMs, targetZone);
    const endLabel = formatTime(endUtcMs, targetZone);

    // Derive dayOffset: compare target-zone calendar date of the interval
    // START against the base-zone calendar date of the same UTC instant.
    const baseDate = getLocalDate(startUtcMs, baseZone);
    const targetDate = getLocalDate(startUtcMs, targetZone);

    let dayOffset = 0;
    if (targetDate > baseDate) dayOffset = 1;
    else if (targetDate < baseDate) dayOffset = -1;

    return { label: `${startLabel}–${endLabel}`, dayOffset };
  } catch {
    // CR-01 defense-in-depth: an invalid IANA zone id makes Intl.DateTimeFormat
    // throw RangeError during render. Stored ids are validated upstream
    // (slaPricingConfigurationService.ts's assertValidTimezone), but a bad
    // zone must degrade to a placeholder cell rather than take down the
    // whole editor page render.
    return { label: '—', dayOffset: 0 };
  }
}
